import type { DataSourceLoadOptions } from '@sleed/bizcommerce-api';

/**
 * Advanced product-filter model for the grid Filters side-sheet. Mirrors the legacy admin's
 * `NewProductFilters.js` field set. Every field maps to a `getAllGrid` loadOptions field read
 * by `DataSourceLoadOptionsHttpBinder` (`Core/server/API/DataSourceLoadOptions.cs`).
 *
 * Tri-state numbers follow the binder/legacy convention: `1` = yes, `2` = no, `null` = any.
 */
export interface ProductFilters {
	/** → loadOptions.searchByBarcode (binder also accepts `searchBarcode`). */
	barcode: string;
	/** → loadOptions.productCategories (int[]). Stored with labels for chip rendering. */
	categories: { id: number; label: string }[];
	/** → loadOptions.statuses (int[]). */
	statuses: number[];
	/** → loadOptions.subTypes (int[]). */
	subTypes: number[];
	/** → loadOptions.availabilityStatus (enum NAME[], e.g. "Exhausted"). */
	availability: string[];
	/** → loadOptions.isVisible (tri-state 1/2). */
	isVisible: 1 | 2 | null;
	/** → loadOptions.hasImage (tri-state 1/2). */
	hasImage: 1 | 2 | null;
	/** → loadOptions.priceFrom / priceTo (decimal). */
	priceFrom: number | null;
	priceTo: number | null;
	/** → loadOptions.discountFrom / discountTo (int). */
	discountFrom: number | null;
	discountTo: number | null;
	/** → loadOptions.quantityFrom / quantityTo (int). */
	quantityFrom: number | null;
	quantityTo: number | null;
	/** → loadOptions.releaseDateFrom / releaseDateTo (ISO yyyy-MM-dd). */
	releaseDateFrom: string | null;
	releaseDateTo: string | null;
}

export const EMPTY_FILTERS: ProductFilters = {
	barcode: '',
	categories: [],
	statuses: [],
	subTypes: [],
	availability: [],
	isVisible: null,
	hasImage: null,
	priceFrom: null,
	priceTo: null,
	discountFrom: null,
	discountTo: null,
	quantityFrom: null,
	quantityTo: null,
	releaseDateFrom: null,
	releaseDateTo: null
};

/** Counts how many filter groups are active (drives the `Φίλτρα [n]` badge). */
export function activeFilterCount(f: ProductFilters): number {
	let n = 0;
	if (f.barcode.trim()) n++;
	if (f.categories.length) n++;
	if (f.statuses.length) n++;
	if (f.subTypes.length) n++;
	if (f.availability.length) n++;
	if (f.isVisible !== null) n++;
	if (f.hasImage !== null) n++;
	if (f.priceFrom !== null || f.priceTo !== null) n++;
	if (f.discountFrom !== null || f.discountTo !== null) n++;
	if (f.quantityFrom !== null || f.quantityTo !== null) n++;
	if (f.releaseDateFrom !== null || f.releaseDateTo !== null) n++;
	return n;
}

export function hasAnyFilter(f: ProductFilters): boolean {
	return activeFilterCount(f) > 0;
}

/**
 * Applies the advanced filters onto a `getAllGrid` loadOptions envelope.
 *
 * Most fields exist directly on the generated `DataSourceLoadOptions` type and bind via the
 * generated client's query serialization (repeated keys for arrays — the binder's `JoinValues`
 * collapses them to CSV, then splits). `availabilityStatus` is the exception: the generated type
 * is `Array<AvailabilityStatus>` (numeric), but the binder parses enum NAMES, so we assign a
 * `string[]` of names via the same `Record<string, unknown>` cast `applyChip` uses.
 */
export function applyFilters(loadOptions: DataSourceLoadOptions, f: ProductFilters): void {
	const mutable = loadOptions as Record<string, unknown>;

	if (f.barcode.trim()) mutable.searchByBarcode = f.barcode.trim();
	if (f.categories.length) mutable.productCategories = f.categories.map(c => c.id);
	if (f.statuses.length) mutable.statuses = f.statuses;
	if (f.subTypes.length) mutable.subTypes = f.subTypes;
	// Enum NAMES (not numbers) — binder does Enum.TryParse(name). See note above.
	if (f.availability.length) mutable.availabilityStatus = f.availability;
	if (f.isVisible !== null) mutable.isVisible = f.isVisible;
	if (f.hasImage !== null) mutable.hasImage = f.hasImage;
	if (f.priceFrom !== null) mutable.priceFrom = f.priceFrom;
	if (f.priceTo !== null) mutable.priceTo = f.priceTo;
	if (f.discountFrom !== null) mutable.discountFrom = f.discountFrom;
	if (f.discountTo !== null) mutable.discountTo = f.discountTo;
	if (f.quantityFrom !== null) mutable.quantityFrom = f.quantityFrom;
	if (f.quantityTo !== null) mutable.quantityTo = f.quantityTo;
	if (f.releaseDateFrom !== null) mutable.releaseDateFrom = f.releaseDateFrom;
	if (f.releaseDateTo !== null) mutable.releaseDateTo = f.releaseDateTo;
}

// ── URL (de)serialization ────────────────────────────────────────────────────
// Persisted under a single `f` searchParam as compact JSON so the grid's existing
// chip/page/search params stay untouched. Category labels are persisted alongside ids
// so chips render without an extra lookup round-trip on reload.

const FILTER_PARAM = 'f';

export function filtersToParam(f: ProductFilters): string | null {
	if (!hasAnyFilter(f)) return null;
	const compact: Record<string, unknown> = {};
	if (f.barcode.trim()) compact.bc = f.barcode.trim();
	if (f.categories.length) compact.cat = f.categories.map(c => [c.id, c.label]);
	if (f.statuses.length) compact.st = f.statuses;
	if (f.subTypes.length) compact.sub = f.subTypes;
	if (f.availability.length) compact.av = f.availability;
	if (f.isVisible !== null) compact.vis = f.isVisible;
	if (f.hasImage !== null) compact.img = f.hasImage;
	if (f.priceFrom !== null) compact.pf = f.priceFrom;
	if (f.priceTo !== null) compact.pt = f.priceTo;
	if (f.discountFrom !== null) compact.df = f.discountFrom;
	if (f.discountTo !== null) compact.dt = f.discountTo;
	if (f.quantityFrom !== null) compact.qf = f.quantityFrom;
	if (f.quantityTo !== null) compact.qt = f.quantityTo;
	if (f.releaseDateFrom !== null) compact.rf = f.releaseDateFrom;
	if (f.releaseDateTo !== null) compact.rt = f.releaseDateTo;
	return JSON.stringify(compact);
}

export function filtersFromParams(params: URLSearchParams): ProductFilters {
	const raw = params.get(FILTER_PARAM);
	if (!raw) return { ...EMPTY_FILTERS };
	let compact: Record<string, unknown>;
	try {
		compact = JSON.parse(raw) as Record<string, unknown>;
	} catch {
		return { ...EMPTY_FILTERS };
	}
	const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
	const tri = (v: unknown): 1 | 2 | null => (v === 1 || v === 2 ? v : null);
	const intArr = (v: unknown): number[] => (Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []);
	const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
	const catArr = (v: unknown): { id: number; label: string }[] =>
		Array.isArray(v)
			? v
					.filter((x): x is [number, string] => Array.isArray(x) && typeof x[0] === 'number')
					.map(([id, label]) => ({ id, label: typeof label === 'string' ? label : String(id) }))
			: [];

	return {
		barcode: typeof compact.bc === 'string' ? compact.bc : '',
		categories: catArr(compact.cat),
		statuses: intArr(compact.st),
		subTypes: intArr(compact.sub),
		availability: strArr(compact.av),
		isVisible: tri(compact.vis),
		hasImage: tri(compact.img),
		priceFrom: num(compact.pf),
		priceTo: num(compact.pt),
		discountFrom: num(compact.df),
		discountTo: num(compact.dt),
		quantityFrom: num(compact.qf),
		quantityTo: num(compact.qt),
		releaseDateFrom: typeof compact.rf === 'string' ? compact.rf : null,
		releaseDateTo: typeof compact.rt === 'string' ? compact.rt : null
	};
}

export { FILTER_PARAM };
